import { html } from '@shared/utils/rendering/html';
import Form from '@shared/components/forms';
import Button from '@shared/components/buttons';

import { useRef, useMemo, useCallback } from 'preact/hooks';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import { subcomponentFormOptions } from '../../../../../../configs/forms/subcomponents/subcomponent-form.config';

import { useCache } from '../../../../../../utils/context/cache-context';
import { CacheKeyNames } from '../../../../../../constants/utility/cache-key-names';
import { RatingsFromId } from '../../../../../../constants/chips/ratings.constants';
import { usePut } from '@core/utils/hooks/use-put';
import { useSubcomponentFormSchema } from '@core/configs/forms/subcomponents/subcomponent-form.schema';
import { useFormConfig } from '@core/utils/forms';
import { onSuccessfulResponse, upsertOne } from '@core/utils/helpers/mutations/mutation-effects';
import { updateEvidence } from './helpers/cache-updates';

const EditEvidenceForm = ({ rowData, ...props }) => {
    const schema = useSubcomponentFormSchema('edit');
    const config = useFormConfig(); // to be used soon.

    const getCache = useCache().getKey;
    const queryClient = useQueryClient();

    const formRef = useRef(undefined);

    const { put: update, isSubmitting: isUpdating } = usePut('/evidence', { asFormData: true });

    // Early return if no required data
    if (!rowData?.attestationRecordId) {
        return html`
            <div class="card-header"><h4>Edit Evidence</h4></div>
            <div class="card-body">
                <p>Unable to load form: Missing required data.</p>
            </div>
        `;
    }

    const formData = useMemo(() => {
        if (!rowData) return {};

        const ratingObject = RatingsFromId(rowData?.ratingId);
        return {
            comment: rowData?.comment || '',
            rating: ratingObject ? [ratingObject] : [],
            uploadEvidence: rowData?.evidence?.fileName || null,
        };
    }, [rowData]);

    const editMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await update(payload);
            return response?.data ?? response;
        },
        onSuccess: (response) => {
            const key = getCache(CacheKeyNames.legalEntityAssessmentData).cacheKey;
            if (!key && !response) return;
            try {
                onSuccessfulResponse(queryClient, key, updateEvidence, response);
            } catch (error) {
                console.error('Error updating cache:', error);
            }
        },
        onSettled: () => {
            const key = getCache(CacheKeyNames.legalEntityAssessmentData).cacheKey;
            queryClient.invalidateQueries({ queryKey: key });
        },
    });

    const handleSubmit = useCallback(
        (data) => {
            editMutation.mutate({
                ...data,
                attestationRecordId: rowData.attestationRecordId,
                uploadEvidence:
                    typeof data.uploadEvidence === 'string' ? null : data.uploadEvidence,
            });
        },
        [editMutation, rowData.attestationRecordId]
    );
    return html`
        <div class="card-header"><h4>Edit Evidence</h4></div>
        <div class="card-body">
            <${Form}
                ref=${formRef}
                schema=${schema}
                onSubmit=${handleSubmit}
                initialValues=${formData}
                options=${{
                    ...subcomponentFormOptions,
                    formProps: {
                        ...subcomponentFormOptions.formProps,
                        disabled: isUpdating,
                    },
                }}
            />
        </div>
        <div class="card-footer">
            <${Button}
                label=${{ text: 'Reset' }}
                buttonType="secondary"
                disabled=${isUpdating}
                onClick=${() => formRef?.current?.reset()}
            />
            <${Button}
                label=${{ text: 'Update' }}
                buttonType="primary"
                disabled=${isUpdating}
                onClick=${(event) => formRef?.current?.handleSubmit(event)}
            />
        </div>
    `;
};

export default EditEvidenceForm;
